import { useMemo, type VoidFunctionComponent } from 'react';
import { useCanDisplaySecurityOptions } from '../../generic/hooks';
import Subnav from '../../../generic/Subnav';
import { ReportCategories, ReportCategoryTitles } from '../../generic/ReportGeneration/utils';
import { isFreeTier } from '../../../../../shared/subscriptions';
import { window } from '../../../../globals';
import { useIsFeatureEnabled } from '../../../../helpers/features';
import { DISABLE_REPORT_UI_REFRESH, FIX_PLAN_REPORT } from '../../../../../shared/featureFlags/flagNames';

/**
 * Mini hook which just memoizes the enabled report types based on the
 * security and PDF reports feature flag.
 */
const useEnabledReportTypes = () => {
  const isSecurityAndPDFReportsEnabled = useCanDisplaySecurityOptions();
  const isFixPlanReportsEnabled = useIsFeatureEnabled(FIX_PLAN_REPORT);

  return useMemo(() => {
    const excludedReports: string[] = [];
    if (!isSecurityAndPDFReportsEnabled) {
      excludedReports.push(ReportCategories.Vulnerability);
    }
    if (!isFixPlanReportsEnabled) {
      excludedReports.push(ReportCategories.FixPlan);
    }

    return Object.entries(ReportCategoryTitles).filter(([key]) => !excludedReports.includes(key));
  }, [isSecurityAndPDFReportsEnabled, isFixPlanReportsEnabled]);
};

type Props = {
  baseUrl: string;
};

/**
 * ReleaseGroupSubnavItemReports is the Subnav item for the reports section of a release group.
 */
const ReleaseGroupSubnavItemReports: VoidFunctionComponent<Props> = ({
  baseUrl,
}) => {
  const isReportUIRefreshEnabled = !useIsFeatureEnabled(DISABLE_REPORT_UI_REFRESH);
  const shouldShowSettings = isReportUIRefreshEnabled && !isFreeTier({
    plan: window?.user?.organization?.stripe_plan,
    stripePriceLimit: window?.user?.organization?.stripePriceLimit,
  }); // For settings

  const enabledReportTypes = useEnabledReportTypes();
  return (
    <Subnav.Dropdown
      to={`${baseUrl}/reports`}
      navKey="reports"
    >
      <Subnav.Dropdown.RootItem>
        Reports
      </Subnav.Dropdown.RootItem>
      {enabledReportTypes.map(([reportType, title]) => (
        <Subnav.Dropdown.Item
          key={reportType}
          to={`${baseUrl}/reports/${reportType}`}
          navKey={`reports/${reportType}`}
        >
          {title}
        </Subnav.Dropdown.Item>
      ))}
      {shouldShowSettings && (
        <Subnav.Dropdown.Item
          to={`${baseUrl}/reports/settings`}
          navKey="reports/settings"
        >
          Settings
        </Subnav.Dropdown.Item>
      )}
    </Subnav.Dropdown>
  );
};

export default ReleaseGroupSubnavItemReports;
